// Translates between the visual builder's { nodes, edges } graph and the
// backend's linear { trigger, steps } journey config.
//
// Phase-1 scope: the canvas has no drag-to-connect interaction yet (edges are
// always empty), and the backend engine only supports segment-triggered,
// linear step chains — no branching (logic-condition/logic-ab), no webhook
// dispatch, no schedule/event/API triggers. Node order on the canvas (creation
// order) stands in for step order until real edges exist.

const ACTION_NODE_TO_CHANNEL = {
  'action-push': 'push',
  'action-inapp': 'inapp',
  'action-whatsapp': 'whatsapp',
  'action-email': 'email',
  'action-sms': 'sms',
};

const CHANNEL_TO_ACTION_NODE = Object.fromEntries(
  Object.entries(ACTION_NODE_TO_CHANNEL).map(([node, channel]) => [channel, node])
);

export function nodesToJourneyConfig(nodes) {
  const entryNode = nodes.find((n) => n.type.startsWith('entry-'));
  if (!entryNode) throw new Error('Add a trigger (Entry) node before saving.');
  if (entryNode.type !== 'entry-segment') {
    throw new Error('Only "Segment" entry triggers are supported right now.');
  }
  if (!entryNode.cfg?.segmentId) {
    throw new Error('Pick a segment for the entry trigger.');
  }

  const steps = [];
  const skipped = [];
  let pendingWait = null;
  let stepNumber = 1;

  for (const node of nodes) {
    if (node.id === entryNode.id) continue;

    if (node.type === 'wait-relative') {
      pendingWait = { value: Number(node.cfg.amount) || 1, unit: node.cfg.unit || 'hours' };
      continue;
    }

    if (!ACTION_NODE_TO_CHANNEL[node.type]) {
      skipped.push(node.type);
      continue;
    }

    steps.push({
      step: stepNumber++,
      action: 'send_message',
      channel: ACTION_NODE_TO_CHANNEL[node.type],
      template_id: node.id,
      template: node.cfg,
      ...(pendingWait ? { wait_after: pendingWait } : {}),
    });
    pendingWait = null;
  }

  if (!steps.length) {
    throw new Error('Add at least one action step (Push, SMS, Email, WhatsApp, or In-App).');
  }

  return {
    config: { trigger: { segment_id: entryNode.cfg.segmentId, evaluate_every: '15min' }, steps },
    skipped,
  };
}

// Best-effort reverse mapping for loading an existing journey back into the
// canvas — reconstructs one entry-segment node plus one action node (and a
// preceding wait-relative node, if the step had a wait_after) per step.
export function journeyConfigToNodes(config) {
  let nodeCounter = 1;
  const nextId = () => 'n' + nodeCounter++;
  const nodes = [];
  let col = 0;
  let row = 0;
  const place = () => {
    const pos = { x: 60 + col * 240, y: 60 + row * 160 };
    col = (col + 1) % 3;
    if (col === 0) row++;
    return pos;
  };

  nodes.push({ id: nextId(), type: 'entry-segment', ...place(), cfg: { segmentId: config?.trigger?.segment_id || '' } });

  for (const step of config?.steps || []) {
    if (step.wait_after) {
      nodes.push({ id: nextId(), type: 'wait-relative', ...place(), cfg: { amount: String(step.wait_after.value), unit: step.wait_after.unit, waitType: 'Fixed duration' } });
    }
    const actionType = CHANNEL_TO_ACTION_NODE[step.channel] || 'action-push';
    nodes.push({ id: nextId(), type: actionType, ...place(), cfg: step.template || {} });
  }

  return nodes;
}
